import axios from "axios";
import { announceLocalChange } from "@/lib/liveBus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("erp_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach finance token for /sales, /purchases, /rm-costs, and /finance/*
  const url = config.url || "";
  const isFinanceUrl = /(^|\/)(sales|purchases|rm-costs|finance)(\/|$|\?)/.test(url);
  if (isFinanceUrl) {
    const financeTok = sessionStorage.getItem("erp_finance_token");
    if (financeTok) {
      config.headers["X-Finance-Token"] = financeTok;
    }
  }
  return config;
});

// Writes report what they changed (backend/routes/live.py); open screens reload from it.
const announceChanges = (response) => {
  const changed = response?.headers?.["x-changed"];
  if (changed) announceLocalChange(changed.split(","));
};

api.interceptors.response.use(
  (r) => {
    announceChanges(r);
    return r;
  },
  (err) => {
    announceChanges(err?.response);
    if (err?.response?.status === 401) {
      const detail = err?.response?.data?.detail || "";
      // Finance PIN expired/needed → clear finance token and let component handle re-prompt
      if (/finance/i.test(detail) || /Finance/.test(detail)) {
        sessionStorage.removeItem("erp_finance_token");
        return Promise.reject(err);
      }
      localStorage.removeItem("erp_token");
      localStorage.removeItem("erp_user");
      if (window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .join(" · ");
  return String(detail);
}

export function formatINR(value) {
  const n = Number(value || 0);
  return "₹ " + n.toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
}

// Dashboard figures the way people say them: 8.23 L, 2.56 Cr, 63.2 K. Tables keep formatINR.
export function formatShort(value, lakhDigits = 2) {
  const n = Number(value || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`;
  if (a >= 1e5) return `${(n / 1e5).toFixed(lakhDigits)} L`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)} K`;
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

export function formatINRShort(value) {
  return value == null || Number.isNaN(Number(value)) ? "—" : `₹${formatShort(value)}`;
}

// Chart axis ticks: ₹65L, ₹1.3Cr.
export function formatINRAxis(value) {
  const n = Number(value || 0);
  const a = Math.abs(n);
  if (a >= 1e7) return `₹${+(n / 1e7).toFixed(1)}Cr`;
  if (a >= 1e5) return `₹${+(n / 1e5).toFixed(1)}L`;
  if (a >= 1e3) return `₹${+(n / 1e3).toFixed(0)}K`;
  return `₹${n}`;
}

export function formatDate(iso) {
  if (!iso) return "—";
  try {
    // A plain YYYY-MM-DD is a calendar day, not midnight UTC (which shows the day before west of UTC).
    const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = day ? new Date(Number(day[1]), Number(day[2]) - 1, Number(day[3])) : new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
}
